import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import { InputListCondensed } from '../InputListCondensed';
import { AddSource } from '../AddSource';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Tooltip from '@mui/material/Tooltip';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { CircularProgress } from '@mui/material';
import WandIcon from '../../images/wand-icon.png';
import { useFeathers } from '../../app/util';
import { bioNeedsApproval, approveBio, updateBioText } from './review-controls';
import { sourceAfterQuoteEdit, splitQuoteContext } from '../EditStance/split-quote-context';

const noDataOptionsStatic = {
  'no-public-info': 'There is little to no public information on this candidate.',
  'no-response': 'Candidate has no website and has not responded to the questionnaire.',
  'issue-specific': 'Website does not mention this specific information.'
}

const originFromUrl = (url) => {
  if(!url) return null;
  try {
    return new URL(url).origin;
  } catch(e) {
    return null;
  }
}

const EditBioField = React.forwardRef(({
  title,
  value,              // expected to be an object containing properties: text, sources, missingData, and hasHallucinations
  onChange,
  bioType,
  
  active,
  onFocus,
  shouldAutoFocus,
  disabled,

  possibleSources,
  hasWebsite,
  candidateName,
  raceName,           // Required for bio summarization API
  office              // Optional - used for better context
}, inputRef) => {
  // Track the original bio text (machine-written bio)
  const [originalBioText, setOriginalBioText] = useState(null);
  const feathers = useFeathers();
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summarizedTextStaged, setSummarizedTextStaged] = useState(null);
  
  // Initialize original text on mount or when value.text first becomes available
  useEffect(() => {
    if (originalBioText === null && value?.text) {
      setOriginalBioText(value.text);
    }
  }, [value?.text, originalBioText]);

  useEffect(() => {
    if(summarizedTextStaged) {
      setSummarizedTextStaged(null);
      if(value.text && !summarizedTextStaged.force) return;
      onChange({ ...value, text: summarizedTextStaged.summary });
    }
  }, [summarizedTextStaged, value]);
  
  // Calculate if more than 5 characters were deleted from the original
  const deletedCharCount = useMemo(() => {
    if (!originalBioText || !value?.text) return 0;
    return Math.max(0, originalBioText.length - value.text.length);
  }, [originalBioText, value?.text]);
  
  const showInaccuracyQuestion = typeof(value?.hasHallucinations) === 'boolean' || value?.hasHallucinations === null || deletedCharCount > 5;

  const canSummarize = useMemo(() => {
    console.log('canSummarize', value?.sources?.length > 0, value.sources.some(s => s.directQuote));
    return value?.sources?.length > 0 && value.sources.some(s => s.directQuote);
  }, [value]);
  const showApprove = bioNeedsApproval(value);

  const summarizeText = async (force) => {
    setSummaryLoading(true);
    try {
      // Format sources for the API
      const formattedSources = value.sources
        .filter(source => source.directQuote) // Only include sources with quotes
        .map((source) => {
          const sourceTitle = source.sourceType === 'website' 
            ? 'Candidate website' 
            : (source.title || 'Article');
          
          return {
            quote: source.directQuote,
            url: source.url || '',
            sourceTitle
          };
        });
      
      
      const requestData = {
        mode: 'bio',
        candidateName,
        bioType,
        raceName: raceName || office || 'Unknown Race',
        sources: formattedSources,
      };
      
      console.log('Bio summarize request:', requestData);
      
      const { bio } = await feathers.getService('summarize').create(requestData);
      
      setSummarizedTextStaged({
        summary: (bio || ''),
        force,
      });
    } catch(err) {
      console.log(`summarize failed`, err);
    } finally {
      setSummaryLoading(false);
    }
  };
  
  const noDataOptions = useMemo(() => {
    const keys = hasWebsite ? ['issue-specific'] : ['no-response', 'no-public-info'];
    return keys.reduce((acc, key) => {
      acc[key] = noDataOptionsStatic[key];
      return acc;
    }, {})
  }, [ hasWebsite ])

  const uniqueSources = useMemo(() => {
    if (!value?.sources) return [];
    const uniqueHostNames = new Set(value.sources.map(s => originFromUrl(s.url)).filter(Boolean));
    return Array.from(uniqueHostNames);
  }, [value?.sources]);

  const checkBox = (e) => {
    onChange({ ...value, missingData: e.target.checked ? null : Object.keys(noDataOptions)[0] })
  }

  const onChangeMissingReason = (e) => {
    onChange({ ...value, missingData: e.target.value })
  }
  const onChangeText = (e) => {
    onChange(updateBioText(value, e.target.value))
  }

  useEffect(() => {
    if(value.missingData) {
      if(!noDataOptions[value.missingData]) {
        onChange({ ...value, missingData: Object.keys(noDataOptions)[0] })
      }
    }
  }, [ hasWebsite, value?.missingData, noDataOptions ])

  return (
    <BioContainer>
      <Wrapper onClick={(e) => onFocus?.(e)} active={active ? 1 : 0} ref={inputRef}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',  scrollMarginTop: '60px' }}>
        <Typography variant={'h4'}>{title}</Typography>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {
            active && showApprove &&
            <Tooltip title='Approve bio'>
              <IconButton
                aria-label='Approve bio'
                size='small'
                disabled={disabled}
                onClick={() => onChange(approveBio(value))}
                style={{ marginRight: '12px'}}
              >
                <CheckCircleIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          }
          <Typography variant='body2'>Has information</Typography>
          <Checkbox
            size='small'
            checked={!Boolean(value.missingData)}
            onChange={checkBox}
            style={{ marginRight: '-12px', marginLeft: '-8px'}}
            inputProps={{ 'aria-label': 'primary checkbox' }}
          />
        </div>
      </div>
      {
        value.missingData &&
        <FormControl>
          <Select
            variant='outlined'
            value={value?.missingData}
            onChange={onChangeMissingReason}
            size='small'
          >
            {
              Object.keys(noDataOptions).map(option => (
                <MenuItem key={option} value={option}>{noDataOptions[option]}</MenuItem>
              ))
            }
          </Select>
        </FormControl>
      }
      {
        !value.missingData && <>
          <TextField
            disabled={disabled}
            onChange={onChangeText}
            value={value?.text || ''}
            multiline
            minRows={active ? 3 : 1}
            variant='outlined'
            color='primary'
            sx={!active ? {
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
              '& .MuiInputBase-root': { padding: '0px 0px 0px 0px' },
              '& .MuiInputBase-input': { padding: '0px 0px 0px 0px' }
            } : undefined}
            onFocus={(e) => onFocus?.(e)}
            autoFocus={shouldAutoFocus}
          />
          {
            active && (
              <div style={{ display: 'flex', marginTop: '4px', gap: '12px', alignItems: 'center' }}>
                <Tooltip title={'Summarize using AI' + (!canSummarize ? '. Must add source(s) with direct quote(s).' : '')} placement='right'>
                  <IconButton size='small' onClick={canSummarize ? () => summarizeText(true) : undefined}>
                    {
                      summaryLoading &&
                      <CircularProgress
                        color='#000000'
                        size={15}
                        style={{ alignSelf: 'center' }}
                      />
                    }
                    {
                      !summaryLoading &&
                      <img src={WandIcon} style={{ width: '20px', height: '20px', ...((summaryLoading || !canSummarize) ? { opacity: 0.3 } : {}) }} />
                    }
                  </IconButton>
                </Tooltip>
              </div>
            )
          }
          {
            showInaccuracyQuestion && (
              <InaccuracyQuestionWrapper>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  Did this original bio have inaccurate details?
                </Typography>
                <RadioGroup
                  row
                  value={value?.hasHallucinations === undefined || value?.hasHallucinations === null ? '' : value.hasHallucinations.toString()}
                  onChange={(e) => {
                    const newValue = e.target.value === '' ? undefined : e.target.value === 'true';
                    onChange({ ...value, hasHallucinations: newValue });
                  }}
                >
                  <FormControlLabel 
                    value="true" 
                    control={<Radio size="small" />} 
                    label={<Typography variant='body2'>Yes</Typography>}
                  />
                  <FormControlLabel 
                    value="false" 
                    control={<Radio size="small" />} 
                    label={<Typography variant='body2'>No</Typography>}
                  />
                </RadioGroup>
              </InaccuracyQuestionWrapper>
            )
          }
          {
            active ? (
              <SourceWrapper>
                {value.sources.map((source, idx) => (
                  <SourceWithQuote 
                    key={`SourceWithQuote-${idx}-${source.url}`} 
                    source={source} 
                    onFocus={onFocus} 
                    onRemove={() => {
                      onChange({ ...value, sources: value.sources.filter((_, i) => i !== idx) })
                    }}
                    onChange={(updatedSource) => {
                      const updatedSources = [...value.sources];
                      updatedSources[idx] = updatedSource;
                      onChange({ ...value, sources: updatedSources });
                    }}
                    possibleSources={possibleSources}
                  />
                ))}
                <Button 
                  color='primary'
                  size='small'
                  sx={{
                    alignSelf: 'flex-start',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '& .MuiButton-startIcon': {
                      marginRight: '4px'
                    }
                  }}
                  fullWidth={false}
                  onClick={() => {
                    onChange({ ...value, sources: [...value.sources, { url: '', directQuote: '' }] })
                  }}
                  startIcon={<AddIcon />}
                >
                  Add quote
                </Button>
              </SourceWrapper>
            ) : (
              <CondensedView>
                <Typography variant="body2" color="textSecondary">
                  {value.sources.length} quotes from {uniqueSources.length} different sources
                </Typography>
              </CondensedView>
            )
          }
        </>
      }
      </Wrapper>
      {
        !active && showApprove &&
        <Tooltip title='Needs review'>
          <WarningAmberIcon color='warning' sx={{ fontSize: '20px', position: 'absolute', right: '-22px' }} />
        </Tooltip>
      }
    </BioContainer>
  )
})

const SourceWithQuote = ({ source, onFocus, onRemove, onChange, possibleSources }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [quote, setQuote] = useState(source.directQuote || '');
  const [quoteContext, setQuoteContext] = useState(source.quoteContext || '');
  const [selectedSource, setSelectedSource] = useState(source.url || '');
  const showQuoteContext = isFocused && Boolean(quoteContext);

  const handleQuoteChange = (e) => {
    const newQuote = e.target.value;
    setQuote(newQuote);
    setQuoteContext('');

    const updatedSource = sourceAfterQuoteEdit(source, newQuote);
    
    onChange?.(updatedSource);
  };

  const handleSourceChange = (e) => {
    const sourceFullAsLink = possibleSources.find(ps => ps?.value?.url === e.target.value)?.value;
    if(sourceFullAsLink) {
      const convertedToSource = {
        ...sourceFullAsLink,
        // Preserve the existing specific URL if it exists and matches the base URL
        url: source.url && source.url.startsWith(e.target.value) ? source.url : e.target.value,
        sourceType: ['website','questionnaire'].includes(sourceFullAsLink?.mediaType)
            ? sourceFullAsLink?.mediaType
            : 'other'
      }
      setSelectedSource(e.target.value);
      const updatedSource = { ...source, ...convertedToSource };
      onChange?.(updatedSource);
    }
  };

  useEffect(() => {
    if(source.url) {
      // find the possibel source that matches the url
      let possibleSource = possibleSources.find(ps => ps.value.url === source.url);
      if(!possibleSource) {
        possibleSource = possibleSources.find(ps => originFromUrl(ps.value.url) === originFromUrl(source.url));
      }
      if(possibleSource) {
        setSelectedSource(possibleSource.value.url);
      }
    }
  }, [source.url, possibleSources])

  useEffect(() => {
    setQuote(source.directQuote || '');
  }, [source.directQuote])

  useEffect(() => {
    setQuoteContext(source.quoteContext || '');
  }, [source.quoteContext])

  return (
    <SourceRow>
      <QuoteField>
        <QuoteTextField
          value={showQuoteContext ? quoteContext : quote}
          onChange={handleQuoteChange}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          onBlur={() => setIsFocused(false)}
          multiline={isFocused}
          rows={isFocused ? undefined : 1}
          placeholder="Add quote..."
          variant="standard"
          size="small"
          fullWidth
          sx={showQuoteContext ? {
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            '& .MuiInputBase-input': {
              color: 'transparent',
              caretColor: '#000000'
            }
          } : undefined}
        />
        {showQuoteContext && (
          <QuoteContextDisplay aria-hidden="true">
            {splitQuoteContext(quoteContext, quote).map((part, idx) => (
              part.isDirectQuote
                ? <strong key={idx}>{part.text}</strong>
                : <React.Fragment key={idx}>{part.text}</React.Fragment>
            ))}
          </QuoteContextDisplay>
        )}
      </QuoteField>
      <SourceSelect
        value={selectedSource}
        onChange={handleSourceChange}
        size="small"
        input={<SourceInput />}
        displayEmpty
        sx={{
          fontSize: '0.875rem',
          '& .MuiSelect-select': {
            py: 0.5
          }
        }}
      >
        <MenuItem value="" disabled>
          Choose source
        </MenuItem>
        {possibleSources.map(({ value, displayAs}, idx) => (
          <MenuItem value={value?.url} key={idx}>{displayAs}</MenuItem>
        ))}
      </SourceSelect>
      <IconButton 
        size="small" 
        onClick={onRemove} 
        sx={{ 
          ml: 0.5,
          p: 0.5,
          '& .MuiSvgIcon-root': {
            fontSize: '0.875rem',
            color: 'rgba(0, 0, 0, 0.54)'
          },
          '&:hover .MuiSvgIcon-root': {
            color: 'rgba(0, 0, 0, 0.87)'
          }
        }}
      >
        <CancelIcon />
      </IconButton>
    </SourceRow>
  );
};

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
  width: 100%;
`;

const QuoteField = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
`;

const QuoteContextDisplay = styled.div`
  min-height: 24px;
  padding: 4px 0;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const QuoteTextField = styled(TextField)`
  background-color: transparent;
  & .MuiInput-root {
    font-size: 0.875rem;
    background-color: transparent;
  }
`;

const SourceSelect = styled(Select)`
  background-color: white;
  min-width: 180px;
  max-width: 180px;
  width: 180px;
  & .MuiSelect-select {
    padding: 0px 0px 2px 0px;
    padding-right: 20px !important;
    font-size: 0.875rem;
  }
  & .MuiSelect-icon {
    right: 0;
  }
`;

const SourceInput = styled(OutlinedInput)`
  padding: 4px 0 2px 8px;
  border-color: transparent;
  border-radius: 18px;
  font-size: 0.875rem;
`;

const SourceWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  gap: 2px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-top: 6px;
`;

const BioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  position: relative;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  flex: 1;
  padding: ${({ active }) => active ? '12px 16px' : '12px 8px'};
  border-radius: 8px;
  transition: all 0.15s ease;

  ${({ active }) => active ? `
    background-color: #C3CEEC;
    transform: scale(1.05);
  ` : `
    opacity: 1.0;
  `}
`;

const CondensedView = styled.div`
  padding: 8px 0;
`;

const InaccuracyQuestionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin: 2px 0 4px;
`;

export { EditBioField };